import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageAutoRestart({ navigate }) {
  return (
    <div>
      <p className="text-sm text-brand-400 font-medium mb-2">Tools</p>
      <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-3">Auto Restart</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Long unattended sessions end in the dullest ways: a disconnect drops you on the character
        screen, or the game slowly gets tired after hours of map changes. Auto Restart handles both.
        On the character screen it logs in with the character you picked, and on a timer it has the
        MortisEngine loader close the game, start it again and pick the farm back up. It is on the{' '}
        <strong className="text-white">TOOLS &rsaquo; Auto restart</strong> tab.
      </p>

      <Callout type="info">
        One switch does both jobs. Logging in by itself and restarting on a timer belong together:
        a restart the bot cannot log back in from would just leave the game sitting on the
        character screen.
      </Callout>

      <DocHeading>Before You Turn It On</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The bot cannot restart the game on its own &mdash; it closes together with the game. The
        loader does the closing and the relaunching, so it has to be there:
      </p>
      <DocTable
        headers={['You need', 'Why']}
        rows={[
          [<strong className="text-white">The loader open and logged in</strong>, 'It watches for the restart request, and it needs your login and an active subscription to inject again. There is nothing to set in the loader itself.'],
          [<strong className="text-white">The game started from the official launcher, then injected with that loader</strong>, 'At injection the bot remembers how the game was started, and the loader relaunches the game exactly that way.'],
          [<strong className="text-white">Inject again after a manual login</strong>, 'If you log in through the launcher again, the previous session stops working. Inject once into the new game and the restart uses the fresh one.'],
          [<strong className="text-white">A route that starts with a teleport</strong>, 'Only if you want the farm to continue. After a restart you are usually not on the route’s maps (often in town). See Getting Back to the Route below.'],
        ]}
      />

      <DocHeading>Set It Up</DocHeading>
      <Step n={1} title="Turn on Log in and restart by itself">
        While it is off, the card only says{' '}
        <em>Off: the bot never logs in and never restarts the game on its own.</em>
      </Step>
      <Step n={2} title="Go to the character screen and pick a character">
        Your character list can only be read there. Open the menu, go to{' '}
        <strong>TOOLS &rsaquo; Auto restart</strong> and choose from the <strong>Character</strong>{' '}
        dropdown. Until you choose, the first line reads <strong>(pick a character)</strong> &mdash;
        the bot never guesses. If you walked to the character screen yourself with the route
        stopped, it will not log you in this time (see below): log in by hand once, and from then
        on it uses your choice.
      </Step>
      <Step n={3} title="Set Restart every">
        Anywhere from 0 to 480 minutes. <strong>0</strong> means it only logs in and never restarts
        the game (hover the info marker at 0: <em>0 = it only logs in, and never restarts by
        itself.</em>).
      </Step>
      <Step n={4} title="Start your route as usual">
        When the timer runs out, the game is restarted, the bot logs back in, and the route starts
        again &mdash; but only if it was running before the restart.
      </Step>

      <Shot
        name="auto-restart-01-tab.png"
        tab="TOOLS › Auto restart"
        caption="Auto restart switched on: the character, the interval and what is happening now."
        shoot="A TOOLS › Auto restart fül játékban (nem a karakterválasztóban), bekapcsolt „Log in and restart by itself” kapcsolóval: látszik a Character sor a zöld karakternévvel (ehhez a bot ebben a menetben már járjon a karakterválasztóban, különben „N saved” áll ott) és a sor (i) jelölője, a „Restart every” csúszka (pl. 120 min), a Where = in the game és a Next restart = in N min sor."
      />

      <DocHeading>Picking the Character</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Outside the character screen the card cannot show the list, so it shows what it remembers
        instead. Hover the info marker on that line and it explains:{' '}
        <em>The list can only be read on the character screen - go there to pick one. Each account
        keeps its own choice.</em>
      </p>
      <DocTable
        headers={['Character line', 'What it means']}
        rows={[
          [<span className="text-emerald-400">a character name</span>, 'The character it will log in with on this account.'],
          [<span className="text-gray-400">N saved</span>, 'You picked characters before, but it cannot tell which one belongs to this account until it sees the list.'],
          [<span className="text-amber-300">(not set)</span>, 'Nothing picked yet. Visit the character screen and pick one.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        On the character screen the dropdown itself shows the remembered character. If none of the
        names it remembers is on this account (for example the character was renamed or deleted),
        it shows <strong>(pick a character)</strong> again &mdash; pick one.
      </p>
      <Callout type="info">
        <strong>Two accounts, two game windows?</strong> Pick a character in each. The choices are
        kept per account, so they do not overwrite each other, and each window logs in with its own.
      </Callout>

      <DocHeading>The Status Lines</DocHeading>
      <DocTable
        headers={['Line', 'What it shows']}
        rows={[
          [<strong className="text-white">Where</strong>, <><strong>character screen</strong>, <strong>in the game</strong>, or <span className="text-amber-300">cannot tell yet</span> while the bot has not worked out where it is. A moment of that is normal; if it stays, the automatic login cannot work in this session.</>],
          [<strong className="text-white">Next restart</strong>, <>Only when Restart every is above 0. <strong>waiting to get into the world</strong> (the clock has not started), <strong>in N min</strong>, or <span className="text-amber-300">requested</span> when the time is up and the loader has been asked.</>],
          [<strong className="text-white">Last login</strong>, 'What the automatic login did last, or why it is not logging in. See the table below.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        The clock starts when you first get into the game world and then keeps running, map
        changes and loading screens included. Time on the character screen does not count: every
        visit there sets it back to zero, and so does switching the feature off or setting Restart
        every to 0.
      </p>

      <DocHeading>The Countdown on Screen</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        While the switch is on and Restart every is above 0, a small badge sits in the top-right
        corner of the game, even with the menu closed. There is no separate switch for it.
      </p>
      <DocTable
        headers={['Badge', 'What it means']}
        rows={[
          [<strong className="text-white">RESTART&nbsp;&nbsp;--</strong>, 'Not in the world yet, so the clock is not running.'],
          [<strong className="text-white">RESTART&nbsp;&nbsp;1:45:12</strong>, 'Time left until the restart. It turns orange in the last minute.'],
          [<strong className="text-amber-300">RESTART NOW</strong>, 'The time is up and the loader has been asked to restart the game.'],
        ]}
      />

      <DocHeading>What Happens at Restart Time</DocHeading>
      <Step n={1} title="The bot saves your settings">
        Right away, so a switch you flipped a minute ago is not lost.
      </Step>
      <Step n={2} title="The loader closes the game">
        Immediately. It does not wait for the end of a lap or walk you home first, so an unfinished
        lap and any loot still on the ground are left behind.
      </Step>
      <Step n={3} title="The loader starts the game again and injects">
        Just as you did last time, with the same session.
      </Step>
      <Step n={4} title="The bot logs in with your character">
        On the character screen, within a few seconds of the list appearing.
      </Step>
      <Step n={5} title="The route starts again, if it was running">
        The bot loads the route you had open. A client whose route was stopped before the restart
        stays stopped.
      </Step>

      <Callout type="warning">
        <strong>Several game windows restart together.</strong> Closing one game window closes all
        of them, so a restart always covers every open window: the first timer to run out restarts
        the whole group. Only windows injected with MortisEngine come back &mdash; any other game
        window closes and stays closed.
      </Callout>

      <DocHeading>Getting Back to the Route</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        After a restart your character is usually somewhere the route does not know, often in town.
        A route that starts with a teleport handles this by itself: the{' '}
        <button onClick={() => navigate('autopilot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Play tab</button>{' '}
        shows <em>elsewhere - it teleports in</em>, and the automatic start takes you straight in.
        A route without one stops with <strong>You are not on any map of this route.</strong> and
        the farm stays stopped until you come back.
      </p>
      <Callout type="info">
        Before an overnight session, stand in town with your route loaded and look at the Play tab.
        If it says <em>elsewhere - it teleports in</em>, a restart can bring you back. How routes are
        built is explained in{' '}
        <button onClick={() => navigate('route-builder')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Editor &mdash; Building a Route</button>.
      </Callout>

      <DocHeading>When It Logs In &mdash; and When It Does Not</DocHeading>
      <DocTable
        headers={['Situation', 'Logs in?']}
        rows={[
          ['You injected on the character screen (or the loader restarted the game)', <span className="text-emerald-400">Yes</span>],
          ['A disconnect or error dropped you on the character screen while the route was running', <span className="text-emerald-400">Yes</span>],
          ['You went to the character screen yourself while the route was running', <span className="text-emerald-400">Yes &mdash; stop the route first if you really want to leave</span>],
          ['You ended up on the character screen with the route stopped (by hand or by a disconnect)', <span className="text-gray-400">No &mdash; &ldquo;you came here yourself - not logging back in&rdquo;</span>],
          ['No character is picked for this account', <span className="text-gray-400">No &mdash; it tells you so on the Last login line</span>],
          ['The switch is off', <span className="text-gray-400">No. The game waits on the character screen.</span>],
        ]}
      />

      <DocHeading>Last Login Messages</DocHeading>
      <DocTable
        headers={['Message', 'What it means']}
        rows={[
          [<strong className="text-white">name -&gt; ok (try 1/5)</strong>, 'The login request went out. The bot waits up to 8 seconds for the game world to appear, then sends it again (at most 5 times).'],
          [<strong className="text-white">name -&gt; (a short reason) (try 2/5)</strong>, 'The game did not take this request. The bot tries again after a short pause; this also counts toward the 5.'],
          [<strong className="text-white">name: sent 5 times, still here - retrying (round N)</strong>, 'Five requests went out but the game stayed on the character screen. It waits a minute, then tries again.'],
          [<strong className="text-white">name: refused (reason) - retrying (round N)</strong>, 'The game was not ready to accept the login. It waits a minute, then tries again.'],
          [<strong className="text-white">waiting for the character list (N s)</strong>, 'The game has not sent your characters yet. Nothing to do unless this keeps counting for minutes.'],
          [<strong className="text-white">no character picked yet</strong>, 'Pick one in the Character dropdown.'],
          [<strong className="text-white">none of the N saved characters is on this account</strong>, 'You picked characters on other accounts (or the one you picked was renamed or deleted), but none of them is on this one. Pick one here.'],
          [<strong className="text-white">you came here yourself - not logging back in</strong>, 'You left the world with the route stopped, so the bot respects that. Log in by hand.'],
          [<strong className="text-red-400">cannot reach the game thread - login is not possible</strong>, 'The bot cannot act on the character screen in this game session. Close the game, start it again and inject.'],
        ]}
      />
      <Callout type="warning">
        <strong>Round number keeps growing?</strong> A round or two is normal right after a restart.
        If it keeps climbing, log in by hand once.
      </Callout>

      <DocHeading>Why Doesn&rsquo;t It Work?</DocHeading>
      <DocTable
        headers={['What you see', 'What to do']}
        rows={[
          ['Next restart says requested, but the game never closes', 'The loader is closed, not logged in, has no active subscription, or this game was not injected from it. Open the loader and log in; if it did not inject this game, restart the game and inject from it.'],
          ['It worked a few times, then stopped restarting', 'After three failed restarts in a row the loader stops restarting. Fix the cause, then restart the loader.'],
          ['The game restarts but stops at the login screen', 'The saved session is no longer valid, usually because you logged in through the launcher again. Start the game from the launcher and inject once.'],
          ['The game restarts but stays on the character screen', 'Read the Last login line. Most often no character is picked for this account.'],
          ['Back in the game, but the route does not start', 'Either the route was stopped before the restart (by design), or it has no teleport start and you are in town.'],
          ['Next restart stays on waiting to get into the world', 'Normal outside the game world. The clock only runs in the world.'],
          ['You turned it off and are worried a restart is still coming', 'It is not. Switching off, or setting Restart every to 0, cancels a pending restart at once — unless the loader has already started closing the game, which happens within a few seconds of RESTART NOW.'],
        ]}
      />
    </div>
  );
}

export default PageAutoRestart;
